import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Grid } from '@mui/material';
import { useCreateDatasetMutation, useEditDatasetMutation, useGetUsersQuery } from '~/api/v2/hooks';
import { useHasLockedStatus } from '~/hooks/dataset';
import RemoveUserModal from '../DatasetModal/RemoveUserModal';
import DataUploaderForm from './DataUploaderForm';

const DataUploadersBlock = () => {
  const datasetId = +useParams().id!;
  const [removingUploaderIndex, setRemovingUploaderIndex] = useState<number | null>(null);
  const { data } = useGetUsersQuery({});
  const { control, watch } = useFormContext();
  const fieldValues = watch('data_uploaders') ?? [];
  const { hasLockedStatus } = useHasLockedStatus();
  const createDatasetMutation = useCreateDatasetMutation({ fixedCacheKey: `create-dataset` });
  const editDatasetMutation = useEditDatasetMutation({ fixedCacheKey: `edit-dataset` });
  const isSubmitting = editDatasetMutation[1].isLoading || createDatasetMutation[1].isLoading;

  const {
    fields,
    remove: removeDataUploader,
    update: updateDataUploader,
    append: appendDataUploader,
  } = useFieldArray({
    control,
    name: 'data_uploaders',
  });

  const {
    remove: removeDataUploaderNetworkId,
    update: updateDataUploaderNetworkId,
    append: appendDataUploaderNetworkId,
  } = useFieldArray({
    control,
    name: 'data_uploaders_network_ids',
  });

  const appendEmpty = useCallback(() => {
    appendDataUploader('');
    appendDataUploaderNetworkId('');
  }, [appendDataUploader, appendDataUploaderNetworkId]);

  const removeDataUploaderWithNetworkId = (index: number) => {
    if (fields.length === 1 && index === 0) {
      updateDataUploader(0, '');
      updateDataUploaderNetworkId(0, '');

      return;
    }
    removeDataUploader(index);
    removeDataUploaderNetworkId(index);
  };

  const handleRemoveConfirmed = () => {
    if (removingUploaderIndex === null) return;
    removeDataUploaderWithNetworkId(removingUploaderIndex);
    setRemovingUploaderIndex(null);
  };

  const handleTrashClick = (index: number) => {
    if (!datasetId) {
      removeDataUploaderWithNetworkId(index);

      return;
    }

    setRemovingUploaderIndex(index);
  };

  const removingUser =
    removingUploaderIndex === null ? null : data?.find(({ id }) => id === fieldValues[removingUploaderIndex]) || null;

  const isAddDisabled =
    hasLockedStatus || isSubmitting || (fieldValues.length > 0 && !fieldValues[fieldValues.length - 1]);

  useEffect(() => {
    if (!fields.length) appendEmpty();
  }, [appendEmpty, fields.length]);

  return (
    <>
      {!!datasetId && (
        <RemoveUserModal
          user={removingUser}
          open={removingUploaderIndex !== null}
          onRemove={handleRemoveConfirmed}
          onClose={() => setRemovingUploaderIndex(null)}
        />
      )}
      {fields.map((field, index) => (
        <DataUploaderForm
          disabled={isSubmitting || hasLockedStatus}
          key={field.id}
          index={index}
          field={field}
          onRemove={handleTrashClick}
        />
      ))}
      <Grid size={12}>
        <CustomButton buttonType="borderless" onClick={appendEmpty} disabled={isAddDisabled}>
          Add additional data uploaders
        </CustomButton>
      </Grid>
    </>
  );
};

export default DataUploadersBlock;
